import { inject, Injectable } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, forkJoin, map, mergeMap, pipe, switchMap, tap, throwError } from 'rxjs';
import { MarketplaceItemStore } from './marketplace-v2.store';
import { tapResponse } from '@ngrx/operators';
import { LoggingService } from '../../../shared/services/core/logging.service';
import { BasketService } from '../basket.service';
import { BalanceService } from '../../../account/balance/balance.service';
import { TransactionSuccessService } from '../../transaction-success/transaction-success.service';
import { RewardsWalletService } from '../../wallet/rewards-wallet.service';
import { MarketplaceBasketItem } from '../models/basket/marketplace-basket-item.model';
import { config } from '../../../shared/config';
import { Router } from '@angular/router';
import { MarketplaceV2Service } from './marketplace-v2.service';
import { MemberDetails } from '../../../account/member/models/member-details.model';
import { toNumber } from '../../../shared/utility-functions';
import { REWARD_STATUS } from '../../shared/models/reward-status.enum';

/**
 * Service responsible for handling marketplace actions such as fetching items,
 * processing purchases, and updating item statuses. Acts as a facade over
 * MarketplaceService, coordinating store updates, basket management, and
 * post-purchase side effects.
 */
@Injectable({
  providedIn: 'root',
})
export class MarketplaceFacade {
  private readonly marketplaceService = inject(MarketplaceV2Service);
  private readonly marketplaceStore = inject(MarketplaceItemStore);
  private readonly logService = inject(LoggingService);
  private readonly basketService = inject(BasketService);
  private readonly balanceService = inject(BalanceService);
  private readonly transactionSuccessService = inject(TransactionSuccessService);
  private readonly rewardsWalletService = inject(RewardsWalletService);
  private readonly router = inject(Router);

  /** Fetches all marketplace items (v2) and populates the store. Skips the API call if the cache is still valid (TTL 60s). */
  readonly fetchMarketplaceItems = rxMethod<void>(
    pipe(
      switchMap(() => {
        if (this.marketplaceStore.isCacheValid()) {
          return EMPTY;
        }
        return this.marketplaceService.fetchMarketplaceItems().pipe(
          tapResponse({
            next: (marketplaceItems) => this.marketplaceStore.set(marketplaceItems),
            error: (error) => this.logService.error('Failed to fetch marketplace items', error),
          })
        );
      })
    )
  );

  /**
   * Fetches a single marketplace item by ID and upserts it into the store.
   * Navigates to 404 if the item is not found.
   */
  readonly fetchMarketplaceItemById = rxMethod<number>(
    pipe(
      switchMap((id: number) => {
        const current = this.marketplaceStore.selectedItem();
        if (current !== null && current.Id !== toNumber(id)) {
          this.marketplaceStore.setSelectedItem(null);
        }
        return this.marketplaceService.fetchMarketplaceItemById(id).pipe(
          tapResponse({
            next: (marketplaceItem) => {
              this.marketplaceStore.setSelectedItem(marketplaceItem);
            },
            error: (error) => {
              this.logService.error(`Failed to fetch marketplace item with id ${id}`, error);
              this.router.navigate(['/404page']);
            },
          })
        );
      })
    )
  );

  /** Updates the display status of a marketplace item in the store by ID. */
  readonly updateMarketplaceStatus = (id: number, status: REWARD_STATUS) => {
    this.marketplaceStore.updateById(id, { Status: status });
  };

  /**
   * Processes a single-item purchase by clearing the basket, adding the item,
   * and submitting the order. On success, debits the user's loyalty balance,
   * records the transaction, and invalidates the rewards wallet cache.
   */
  purchase(item: any, quantity: number, attributeMappingId?: number, customFields?: any[], useCardToken: boolean = false) {
    this.logService.debug(`Purchasing ${quantity} quantity of marketplace item`, item);

    // Reset basket and add the item being purchased
    this.basketService.emptyBasket();
    this.basketService.addItemToBasket(
      MarketplaceBasketItem.fromMarketplaceItem(item, quantity, attributeMappingId, customFields)
    );
    this.marketplaceStore.setLoading(true);
    return this.marketplaceService.purchaseBasketItems(this.basketService.getCurrentBasket().items, undefined, useCardToken).pipe(
      tap({
        next: (response) => {
          if (response && response.Data.Success) {
            const cost = item.PointValue * quantity;
            // Debit the loyalty balance locally to reflect the spend immediately
            this.balanceService.debitBalance(config.purseConfig.virtualLoyalty, cost);
            this.transactionSuccessService.setTransaction(item, quantity, cost, response.Data.OrderId);
            // Invalidate cached rewards wallet so the next load reflects updated balances
            this.rewardsWalletService.disableRewardsWalletStoreCache();
          }
          this.marketplaceStore.setLoading(false);
        },
        error: () => {
          this.marketplaceStore.setLoading(false);
        },
        finalize: () => {
          this.clearCache();
        },
      })
    );
  }

  /**
   * Initiates checkout for the current basket and immediately updates the order's billing
   * and shipping addresses in parallel. Returns the original checkout response so callers
   * can access the OrderId without needing a second request.
   * Throws if member details are absent — address update requires them.
   */
  checkoutStart(body: any, memberDetails: MemberDetails | undefined) {
    if (!memberDetails) {
      return throwError(() => new Error('Member details are required to start checkout'));
    }
    return this.marketplaceService
      .checkoutStart(body)
      .pipe(mergeMap((response) => this.updateAddressByOrderId(response.OrderId, memberDetails).pipe(map(() => response))));
  }

  /**
   * Updates both billing and shipping addresses for an order in parallel.
   * Both calls must succeed — forkJoin errors if either fails, which prevents
   * an order from having only one address set.
   */
  updateAddressByOrderId(orderId: string, memberDetails: MemberDetails) {
    if (!orderId) {
      return throwError(() => new Error('Checkout failed to start, no order ID returned'));
    }
    return forkJoin([
      this.marketplaceService.updateAdress(orderId, memberDetails),
      this.marketplaceService.updateShippingAdress(orderId, memberDetails),
    ]);
  }

  /** Forces the next item fetch to hit the API by invalidating the store's TTL cache. */
  clearCache(): void {
    this.marketplaceStore.clearCache();
  }
}
